import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { EntityCardContentInfoViewerProps } from "models/propTypes/pageLayoutPropTypes";
import { EntityCardLayoutConstants } from "utils/constants";
//#endregion
//#region components
import ToggleButtonGroup from "../../buttons/toggleButtonGroup";
import { Grid, Hidden, Portal, Theme, useMediaQuery } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const EntityCardContentInfoViewer: React.FC<EntityCardContentInfoViewerProps> = (props) => {
  const getHasFileViewer = () => props.infoViewer?.props && "meta" in props.infoViewer.props;

  const [showInfoViewer, setShowInfoViewer] = React.useState<boolean>(false);

  React.useEffect(() => {
    setShowInfoViewer(!getHasFileViewer());
  }, [props.infoViewer]);

  const fileViewerMount = document.getElementById(EntityCardLayoutConstants.InfoViewerId);
  const fileViewerSmallMount = document.getElementById(EntityCardLayoutConstants.InfoViewerSmallId);
  const mainCardContentContainerMount = document.getElementById(EntityCardLayoutConstants.MainCardContentContainerId);
  const infoViwerContentContainerMount = document.getElementById(EntityCardLayoutConstants.InfoViwerContentContainerId);
  const mainContentToggleMount = document.getElementById(EntityCardLayoutConstants.MainContentToggleId);

  const isScreenWidthMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const [fileViewerTransitioning, setFileViewerTransitioning] = React.useState<boolean>(
    !fileViewerMount?.classList.contains(cssExports.hasContent) && isScreenWidthMd
  );

  React.useEffect(() => {
    fileViewerMount?.addEventListener("transitionend", handleTransitionEnd);
    fileViewerMount?.addEventListener("transitionstart", handleTransitionStart);

    return () => {
      fileViewerMount?.removeEventListener("transitionend", handleTransitionEnd);
      fileViewerMount?.removeEventListener("transitionstart", handleTransitionStart);
    };
  }, [fileViewerMount]);

  const handleTransitionStart = (event: TransitionEvent) => {
    if (event.propertyName === "flex-grow") {
      setFileViewerTransitioning(true);
    }
  };

  const handleTransitionEnd = (event: TransitionEvent) => {
    if (event.propertyName === "flex-grow") {
      setFileViewerTransitioning(false);
    }
  };

  const onShowInformationClick = () => setShowInfoViewer(false);
  const onShowFilesClick = () => setShowInfoViewer(true);

  //#region showHide infoViewer related components if infoViewer is defined
  if (props.infoViewer) {
    if (isScreenWidthMd) {
      fileViewerMount?.classList.add(cssExports.hasContent);
      mainContentToggleMount?.classList.add(cssExports.hidden);
    } else {
      fileViewerMount?.classList.remove(cssExports.hasContent);
      if (getHasFileViewer()) {
        mainContentToggleMount?.classList.remove(cssExports.hidden);
      } else {
        mainContentToggleMount?.classList.add(cssExports.hidden);
      }
    }
  } else {
    fileViewerMount?.classList.remove(cssExports.hasContent);
    mainContentToggleMount?.classList.add(cssExports.hidden);
  }
  //#endregion

  //#region showHide content based on mainCard \ infoViewer toggle
  if (!isScreenWidthMd && showInfoViewer) {
    if (getHasFileViewer()) mainCardContentContainerMount?.classList.add(cssExports.hidden);
    infoViwerContentContainerMount?.classList.remove(cssExports.hidden);
  } else {
    mainCardContentContainerMount?.classList.remove(cssExports.hidden);
    infoViwerContentContainerMount?.classList.add(cssExports.hidden);
  }
  //#endregion

  return (
    <>
      <Portal container={mainContentToggleMount}>
        <Grid item className={cssExports.toggleContainer}>
          <ToggleButtonGroup
            buttons={[
              {
                children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Information),
                onClick: onShowInformationClick,
              },
              {
                children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Image),
                onClick: onShowFilesClick,
              },
            ]}
          />
        </Grid>
      </Portal>
      <Hidden mdUp>
        <Portal container={fileViewerSmallMount}>
          {props.infoViewer && !fileViewerTransitioning && showInfoViewer ? props.infoViewer : <></>}
        </Portal>
      </Hidden>
      <Hidden mdDown>
        <Portal container={fileViewerMount}>
          {props.infoViewer && !fileViewerTransitioning ? props.infoViewer : <></>}
        </Portal>
      </Hidden>
    </>
  );
};

export default EntityCardContentInfoViewer;
